// Pooled particle bursts (Phase 7): one THREE.Points with per-particle
// velocity/life — a single draw call for every effect in flight. Replaces
// v1's per-particle meshes (effects-manager visual half).

import * as THREE from 'three';

const CAPACITY = 256;
const GRAVITY = -3.5;

interface Particle {
  life: number;
  maxLife: number;
  vx: number;
  vy: number;
  vz: number;
}

export class ParticleSystem {
  private readonly points: THREE.Points;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly particles: Particle[] = [];
  private cursor = 0;

  constructor(scene: THREE.Scene) {
    this.positions = new Float32Array(CAPACITY * 3);
    this.colors = new Float32Array(CAPACITY * 3);
    for (let i = 0; i < CAPACITY; i++) {
      this.particles.push({ life: 0, maxLife: 1, vx: 0, vy: 0, vz: 0 });
      this.positions[i * 3 + 1] = -100; // Park dead particles below ground
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.14,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    });
    material.userData.noToon = true;
    this.points = new THREE.Points(geometry, material);
    this.points.frustumCulled = false;
    this.points.userData.noToon = true;
    scene.add(this.points);
  }

  /** Spherical burst; upward bias makes pickups feel poppy. */
  burst(position: THREE.Vector3, colorHex: string | number, count = 10, speed = 2.2): void {
    const color = new THREE.Color(colorHex);
    for (let n = 0; n < count; n++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % CAPACITY;
      const p = this.particles[i];
      if (!p) continue;
      const theta = Math.random() * Math.PI * 2;
      const up = 0.4 + Math.random() * 0.8;
      p.life = p.maxLife = 0.45 + Math.random() * 0.35;
      p.vx = Math.cos(theta) * speed * (0.4 + Math.random() * 0.6);
      p.vz = Math.sin(theta) * speed * (0.4 + Math.random() * 0.6);
      p.vy = up * speed;
      this.positions[i * 3] = position.x;
      this.positions[i * 3 + 1] = position.y + 0.3;
      this.positions[i * 3 + 2] = position.z;
      this.colors[i * 3] = color.r;
      this.colors[i * 3 + 1] = color.g;
      this.colors[i * 3 + 2] = color.b;
    }
  }

  update(dt: number): void {
    let any = false;
    for (let i = 0; i < CAPACITY; i++) {
      const p = this.particles[i];
      if (!p || p.life <= 0) continue;
      any = true;
      p.life -= dt;
      p.vy += GRAVITY * dt;
      this.positions[i * 3] = (this.positions[i * 3] ?? 0) + p.vx * dt;
      this.positions[i * 3 + 1] = (this.positions[i * 3 + 1] ?? 0) + p.vy * dt;
      this.positions[i * 3 + 2] = (this.positions[i * 3 + 2] ?? 0) + p.vz * dt;
      if (p.life <= 0) this.positions[i * 3 + 1] = -100;
    }
    if (any) {
      (this.points.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
      (this.points.geometry.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    }
  }
}
